import type { ReactNode } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button, Tag } from '@carbon/react';
import { ChartLine, Close, MappingClear } from '@carbon/react/icons';
import {
  clearSectionAtom,
  disarmCrossSectionAtom,
  sectionModeAtom,
} from './atoms';
import type { SectionMode } from './types';
import styles from './cross-section-drawer.module.scss';

/** How the two modes read in the header pill. */
const MODE_LABELS: Record<SectionMode, string> = {
  arbitrary_line: 'Arbitrary Line',
  well_to_well: 'Well to Well',
};

export interface CrossSectionDrawerProps {
  /** The header's display and view controls, once there is a section to act on. */
  headerControls?: ReactNode;
  /** The profile, the wells list, and anything else the body holds. */
  children?: ReactNode;
}

/**
 * The bottom drawer the Cross-Section lives in. The map stays visible above it,
 * which is the point: the two views are the same section in two projections.
 *
 * Closing the drawer disarms the tool — there is no drawer-open-but-tool-off state,
 * because the drawer is the tool's only surface.
 *
 * The split against the map is the host's to own: it renders this inside
 * `SearchPageLayout` with a `DraggableDivider`, the same pairing the map-query page
 * uses, rather than the drawer measuring itself.
 *
 * @param {CrossSectionDrawerProps} props - Component props.
 * @return {JSX.Element} The drawer.
 */
export const CrossSectionDrawer = ({
  headerControls,
  children,
}: CrossSectionDrawerProps): JSX.Element => {
  const mode = useAtomValue(sectionModeAtom);
  const disarm = useSetAtom(disarmCrossSectionAtom);
  const clearSection = useSetAtom(clearSectionAtom);

  return (
    <section className={styles.drawer} aria-label="Cross section">
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <h3>Cross Section</h3>
          {mode && (
            <Tag type="cool-gray" className={styles.modePill}>
              {MODE_LABELS[mode]}
            </Tag>
          )}
        </div>

        {/* Display and view controls only mean something once a section exists. */}
        {mode && <div className={styles.headerControls}>{headerControls}</div>}

        <div className={styles.headerActions}>
          {mode && (
            <>
              {/* MappingClear rather than a remove glyph: this discards a drawn
                  geometry rather than taking a row off the section. Neither is a
                  delete — nothing saved goes away. */}
              <Button
                kind="ghost"
                size="sm"
                renderIcon={MappingClear}
                onClick={() => clearSection()}
              >
                Clear Cross Section
              </Button>
            </>
          )}
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Close}
            iconDescription="Close cross section"
            tooltipPosition="left"
            onClick={() => disarm()}
          />
        </div>
      </header>

      <div className={styles.body}>
        {mode === null ? (
          // Cleared, so there is nothing to plot and no gesture to instruct yet. The
          // question is on the map beside the tool button; this says where to look
          // rather than repeating it, since the same choice twice is two dismissals.
          <div className={styles.placeholder}>
            <ChartLine size={28} />
            <p>
              Choose how to build the next section in the map, then click to
              define it.
            </p>
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
};
